import { CreditCardType } from "credit-card-type/src/types";
import { ControllerProps } from "react-hook-form";

import { CreditCardNetwork } from "chameleon/lib/components/display-cards/display-cards";

import { cardNetwork } from "checkout-experience/utils/validators/card-network";
import { cardNumber } from "checkout-experience/utils/validators/card-number";
import { isFutureExpiration } from "checkout-experience/utils/validators/is-future-expiration";
import { isValidBic } from "checkout-experience/utils/validators/is-valid-bic";
import { isValidEmail } from "checkout-experience/utils/validators/is-valid-email";
import {
  isValidExpiration,
  isValidMonthExpiration,
  isValidMonthForCurrentYear,
  isValidYearExpiration,
  isValidYearForCurrentMonth,
} from "checkout-experience/utils/validators/is-valid-expiration";
import { isValidIban } from "checkout-experience/utils/validators/is-valid-iban";
import { isValidRoutingNumber } from "checkout-experience/utils/validators/is-valid-routing-number";
import { matchesField } from "checkout-experience/utils/validators/matches-field";
import { noInvalidHyphenOrSpace } from "checkout-experience/utils/validators/no-invalid-hyphen-or-space";
import {
  NO_PAN_IN_FIELD,
  NUMERIC_DATE,
  NUMERIC_ONLY,
  UPG_POSTAL,
} from "checkout-experience/utils/validators/patterns";

export const getValidatorsUpg = (
  formId?: string,
  allowedCardNetworks?: CreditCardNetwork[],
  cardType?: Partial<CreditCardType>,
  expiryDateRequired: boolean = true
): Record<string, ControllerProps["rules"]> => ({
  billingTitle: {},
  doAddressesMatch: {},
  billingAddressName: {
    required: true,
    maxLength: 70,
    minLength: 4,
    pattern: NO_PAN_IN_FIELD,
    validate: {
      hasSpace: (value: string) => {
        // return true if value has a space
        return value.trim().includes(" ");
      },
    },
  },
  billingAddressCountry: {
    required: true,
  },
  billingAddressAddressLine1: {
    required: true,
    pattern: NO_PAN_IN_FIELD,
    maxLength: 40,
  },
  billingAddressAddressLine2: {
    pattern: NO_PAN_IN_FIELD,
    maxLength: 40,
  },
  billingAddressCity: {
    required: true,
    pattern: NO_PAN_IN_FIELD,
    maxLength: 40,
  },
  billingAddressAdministrativeArea: {
    required: true,
    maxLength: 3,
  },
  billingAddressAdministrativeAreaGB: {
    required: true,
    maxLength: 100,
  },
  billingAddressAdministrativeAreaUS: {
    required: true,
    maxLength: 2,
    pattern: /^[ a-zA-Z0-9]*$/,
  },
  billingAddressAdministrativeAreaCA: {
    required: true,
    maxLength: 2,
    pattern: /^[ a-zA-Z0-9]*$/,
  },
  billingAddressZip: {
    required: true,
    minLength: 3,
    maxLength: 12, // UPG limit on postal codes
    pattern: UPG_POSTAL,
    validate: {
      isValidZip: noInvalidHyphenOrSpace(),
    },
  },
  billingAddressZipUS: {
    required: true,
    minLength: 5,
    maxLength: 5,
    pattern: NUMERIC_ONLY,
  },
  billingAddressZipCA: {
    required: true,
    minLength: 6,
    maxLength: 7,
    pattern: /^[ A-Z0-9]*$/,
  },
  billingAddressZipGB: {
    required: true,
    minLength: 6,
    maxLength: 8,
    pattern: /^[ A-Z0-9]*$/,
  },
  cardNumber: {
    required: true,
    minLength: cardType?.lengths?.sort()[0] ?? 16,
    maxLength:
      (cardType?.lengths?.sort().slice(-1)[0] ?? 16) +
      (cardType?.gaps?.length ?? 3),
    // pattern: /"^[0-9 ]*$/,
    validate: {
      cardNetwork: cardNetwork({ allowedCardNetworks }),
      cardNumber: cardNumber(),
    },
  },
  expiration: {
    required: expiryDateRequired,
    minLength: expiryDateRequired ? 5 : 0,
    maxLength: 7,
    pattern: NUMERIC_DATE,
    validate: {
      isValidExpiration: isValidExpiration(),
      isFutureExpiration: isFutureExpiration(),
    },
  },
  expirationMonth: {
    required: expiryDateRequired,
    validate: {
      isValidMonthExpiration: isValidMonthExpiration(),
      isValidMonthForCurrentYear: isValidMonthForCurrentYear(
        "expirationYear",
        formId
      ),
    },
  },
  expirationYear: {
    required: expiryDateRequired,
    validate: {
      isValidYearExpiration: isValidYearExpiration(),
      isFutureExpiration: isValidYearForCurrentMonth("expirationMonth", formId),
    },
  },
  securityCode: {
    required: true,
    minLength: 3,
    maxLength: 4,
    pattern: NUMERIC_ONLY,
  },
  nameOnCard: {
    required: true,
    maxLength: 70,
    minLength: 4,
    pattern: NO_PAN_IN_FIELD,
    validate: {
      hasSpace: (value: string) => {
        // return true if value has a space
        return value.trim().includes(" ");
      },
    },
  },
  accountType: {
    required: true,
  },
  nameOnAccount: {
    required: true,
    pattern: NO_PAN_IN_FIELD,
    validate: {
      hasSpace: (value: string) => {
        // return true if value has a space
        return value.trim().includes(" ");
      },
    },
  },
  routingNumber: {
    required: true,
    pattern: NUMERIC_ONLY,
    minLength: 9,
    maxLength: 9,
    validate: {
      isValidRoutingNumber: isValidRoutingNumber(),
    },
  },
  accountNumber: {
    required: true,
    pattern: NUMERIC_ONLY,
    maxLength: 17,
  },
  confirmAccountNumber: {
    required: true,
    maxLength: 17,
    pattern: NUMERIC_ONLY,
    validate: {
      matchesField: matchesField(
        "confirmAccountNumber",
        "accountNumber",
        formId
      ),
    },
  },
  confirmRoutingNumber: {
    required: true,
    pattern: NUMERIC_ONLY,
    minLength: 9,
    maxLength: 9,
    validate: {
      isValidRoutingNumber: isValidRoutingNumber(),
      matchesField: matchesField(
        "confirmRoutingNumber",
        "routingNumber",
        formId
      ),
    },
  },
  linkWithPaze: {
    required: true,
  },
  sepaNameOnAccount: {
    required: true,
    minLength: 4,
    maxLength: 70,
    validate: {
      hasSpace: (value: string) => {
        // return true if value has a space
        return value.trim().includes(" ");
      },
    },
  },
  sepaEmail: {
    required: true,
    validate: {
      isValidEmail: isValidEmail(),
    },
  },
  bic: {
    required: true,
    validate: {
      isValidBic: isValidBic(),
    },
  },
  iban: {
    required: true,
    validate: {
      isValidIban: isValidIban(),
    },
  },
  giftCardNumber: {
    required: true,
    minLength: 12,
    maxLength: 19,
    pattern: NUMERIC_ONLY,
  },
  pinCode: {
    required: false,
    pattern: NUMERIC_ONLY,
  },
});
